import { useCallback, useEffect, useRef, useState, type PointerEvent } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../components/Icon";
import { dirOf, useLang } from "../i18n/lang";
import { useT } from "../i18n/strings";
import type { PublicMedia } from "../api/types";
import v from "./viewer.module.css";

const MAX_SCALE = 4;
const TAP_ZOOM = 2.5;
const SWIPE = 60;
const DISMISS = 110;
const DOUBLE_TAP_MS = 300;

/** Browsers render these originals; anything else (HEIC, RAW) stays on the display copy. */
const RENDERABLE = /^image\/(jpeg|png|webp|gif|avif)$/;

type Point = { x: number; y: number };
type View = { scale: number; x: number; y: number };
const FIT: View = { scale: 1, x: 0, y: 0 };

type Gesture =
  | { kind: "pinch"; distance: number; mid: Point; start: View }
  | { kind: "pan"; from: Point; start: View }
  | { kind: "swipe"; from: Point; dx: number; dy: number };

const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
const midpoint = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Full-screen photos for the customer page: pinch, double-tap and wheel zoom,
 * drag to pan, swipe between photos, swipe down or Back to close. Plain pointer
 * events, because this entry ships without the Mini App's libraries.
 */
export function PhotoViewer({
  images,
  start,
  title,
  onIndex,
  onClose,
}: {
  images: PublicMedia[];
  start: number;
  title: string;
  onIndex: (index: number) => void;
  onClose: () => void;
}) {
  const t = useT();
  const rtl = dirOf(useLang()) === "rtl";
  const [index, setIndex] = useState(start);
  const [view, setView] = useState<View>(FIT);
  const [live, setLive] = useState(false);
  const [drag, setDrag] = useState<Point>({ x: 0, y: 0 });
  const [full, setFull] = useState<Set<string>>(() => new Set());

  const stage = useRef<HTMLDivElement>(null);
  const image = useRef<HTMLImageElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const pointers = useRef(new Map<number, Point>());
  const gesture = useRef<Gesture | null>(null);
  const lastTap = useRef<{ at: number; point: Point } | null>(null);

  const media = images[index];
  const zoomed = view.scale > 1.01;

  /** Pan limits: the photo's edge may reach the screen's edge, never past it. */
  const bound = useCallback((next: View): View => {
    const box = stage.current;
    const img = image.current;
    if (!box || !img) return next;
    const maxX = Math.max(0, (img.offsetWidth * next.scale - box.clientWidth) / 2);
    const maxY = Math.max(0, (img.offsetHeight * next.scale - box.clientHeight) / 2);
    return { scale: next.scale, x: clamp(next.x, -maxX, maxX), y: clamp(next.y, -maxY, maxY) };
  }, []);

  /** Zoom to `scale` while keeping the photo point under `at` (stage coords) still. */
  const zoomAt = useCallback(
    (from: View, scale: number, at: Point): View => {
      const box = stage.current;
      if (!box) return from;
      const next = clamp(scale, 1, MAX_SCALE);
      const rect = box.getBoundingClientRect();
      const cx = at.x - rect.left - rect.width / 2;
      const cy = at.y - rect.top - rect.height / 2;
      const ratio = next / from.scale;
      return bound({ scale: next, x: cx - (cx - from.x) * ratio, y: cy - (cy - from.y) * ratio });
    },
    [bound],
  );

  const go = useCallback(
    (step: 1 | -1) => {
      const next = index + step;
      if (next < 0 || next >= images.length) return;
      setIndex(next);
      setView(FIT);
      onIndex(next);
    },
    [index, images.length, onIndex],
  );

  // Back closes the viewer instead of leaving the page. The close button goes
  // through history too, so the entry never lingers.
  const closed = useRef(onClose);
  closed.current = onClose;
  useEffect(() => {
    history.pushState({ photoViewer: true }, "");
    const onPop = () => closed.current();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const close = () => history.back();

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") history.back();
      else if (event.key === "ArrowRight") go(rtl ? -1 : 1);
      else if (event.key === "ArrowLeft") go(rtl ? 1 : -1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, rtl]);

  // The original is often many megabytes: fetch it only once someone zooms in.
  useEffect(() => {
    if (!zoomed || !media || full.has(media.url) || !RENDERABLE.test(media.mime_type)) return;
    if (!media.display_url || media.display_url === media.url) return;
    const probe = new Image();
    probe.onload = () => setFull((done) => new Set(done).add(media.url));
    probe.src = media.url;
  }, [zoomed, media, full]);

  // Wheel zoom has to be a non-passive native listener to stop the page scrolling.
  useEffect(() => {
    const box = stage.current;
    if (!box) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      setView((current) =>
        zoomAt(current, current.scale * Math.exp(-event.deltaY * 0.002), {
          x: event.clientX,
          y: event.clientY,
        }),
      );
    };
    box.addEventListener("wheel", onWheel, { passive: false });
    return () => box.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  const begin = () => {
    const points = [...pointers.current.values()];
    if (points.length >= 2) {
      gesture.current = {
        kind: "pinch",
        distance: distance(points[0], points[1]),
        mid: midpoint(points[0], points[1]),
        start: view,
      };
      setDrag({ x: 0, y: 0 });
    } else if (points.length === 1) {
      gesture.current = zoomed
        ? { kind: "pan", from: points[0], start: view }
        : { kind: "swipe", from: points[0], dx: 0, dy: 0 };
    } else {
      gesture.current = null;
    }
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // A pointer the browser already released; the gesture still works uncaptured.
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    setLive(true);
    begin();
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const g = gesture.current;
    const points = [...pointers.current.values()];
    if (!g) return;
    if (g.kind === "pinch" && points.length >= 2) {
      const mid = midpoint(points[0], points[1]);
      const scale = g.start.scale * (distance(points[0], points[1]) / g.distance);
      const zoomedView = zoomAt(g.start, scale, g.mid);
      setView(
        bound({
          ...zoomedView,
          x: zoomedView.x + mid.x - g.mid.x,
          y: zoomedView.y + mid.y - g.mid.y,
        }),
      );
    } else if (g.kind === "pan") {
      setView(
        bound({
          scale: g.start.scale,
          x: g.start.x + event.clientX - g.from.x,
          y: g.start.y + event.clientY - g.from.y,
        }),
      );
    } else if (g.kind === "swipe") {
      g.dx = event.clientX - g.from.x;
      g.dy = event.clientY - g.from.y;
      const sideways = Math.abs(g.dx) > Math.abs(g.dy);
      setDrag(sideways ? { x: g.dx, y: 0 } : { x: 0, y: Math.max(0, g.dy) });
    }
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.delete(event.pointerId)) return;
    const g = gesture.current;
    const point = { x: event.clientX, y: event.clientY };

    if (g?.kind === "swipe" && pointers.current.size === 0) {
      const { dx, dy } = g;
      setDrag({ x: 0, y: 0 });
      const moved = Math.hypot(dx, dy) > 10;
      if (Math.abs(dx) > SWIPE && Math.abs(dx) > Math.abs(dy)) {
        // Photos run in reading order: in RTL the next one waits on the left.
        go(dx < 0 !== rtl ? 1 : -1);
      } else if (dy > DISMISS && dy > Math.abs(dx)) {
        close();
      } else if (!moved) {
        tap(point);
      }
    } else if (g?.kind === "pan" && pointers.current.size === 0) {
      if (Math.hypot(point.x - g.from.x, point.y - g.from.y) < 10) tap(point);
    }

    if (pointers.current.size === 0) setLive(false);
    begin();
  };

  const tap = (point: Point) => {
    const now = performance.now();
    const previous = lastTap.current;
    if (previous && now - previous.at < DOUBLE_TAP_MS && distance(previous.point, point) < 30) {
      lastTap.current = null;
      setView((current) => (current.scale > 1.01 ? FIT : zoomAt(current, TAP_ZOOM, point)));
    } else {
      lastTap.current = { at: now, point };
    }
  };

  const toggleZoom = () => {
    const box = stage.current?.getBoundingClientRect();
    if (!box) return;
    setView((current) =>
      current.scale > 1.01
        ? FIT
        : zoomAt(current, TAP_ZOOM, { x: box.left + box.width / 2, y: box.top + box.height / 2 }),
    );
  };

  if (!media) return null;
  const src = full.has(media.url) ? media.url : (media.display_url ?? media.url);
  const dim = drag.y > 0 ? clamp(1 - drag.y / 400, 0.4, 1) : 1;
  const transform = `translate3d(${view.x + drag.x}px, ${view.y + drag.y}px, 0) scale(${view.scale})`;
  const edge = (step: 1 | -1) => (step === 1 ? index < images.length - 1 : index > 0);

  return createPortal(
    <div
      className={v.viewer}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{ ["--dim" as string]: dim }}
    >
      <div className={v.bar}>
        <button
          ref={closeButton}
          type="button"
          className={v.control}
          aria-label={t.actions.close}
          onClick={close}
        >
          <Icon name="close" size={24} />
        </button>
        {images.length > 1 && (
          <span className={v.count} aria-live="polite">
            {t.publicPage.gallery(index + 1, images.length)}
          </span>
        )}
        <button
          type="button"
          className={v.control}
          aria-label={zoomed ? t.publicPage.zoomOut : t.publicPage.zoomIn}
          onClick={toggleZoom}
        >
          <Icon name={zoomed ? "zoomOut" : "zoomIn"} size={24} />
        </button>
      </div>

      <div
        ref={stage}
        className={v.stage}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img
          key={media.url}
          ref={image}
          className={[v.photo, live && v.live].filter(Boolean).join(" ")}
          src={src}
          alt={title}
          draggable={false}
          style={{ transform }}
        />
      </div>

      {images.length > 1 && (
        <>
          <button
            type="button"
            className={`${v.control} ${v.step} ${v.stepPrev}`}
            disabled={!edge(-1)}
            aria-label={t.publicPage.previous}
            onClick={() => go(-1)}
          >
            <Icon name="back" size={24} />
          </button>
          <button
            type="button"
            className={`${v.control} ${v.step} ${v.stepNext}`}
            disabled={!edge(1)}
            aria-label={t.publicPage.next}
            onClick={() => go(1)}
          >
            <Icon name="forward" size={24} />
          </button>
        </>
      )}
    </div>,
    document.body,
  );
}
